// src/pages/effect/component/Section2.tsx
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { isMobile } from "../../../utils/func";

type Item = {
  no: string; // "01" ~ "06"
  title: string;
  desc: string[];
  color: string;
  desc2?: string[];
};

const Step = ({ item, bgRef }: { item: Item; bgRef: any }) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(72);
  const [locked, setLocked] = useState(false);

  const { scrollYProgress } = useScroll({
    target: rowRef,
    offset: ["start 0.7", "end 0.3"],
  });

  const fillHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const SHOW_START = 0.22;
  const SHOW_FULL = 0.55; //

  const RISE = 56;

  const SCALE_FROM_IND = 0.96;
  const SCALE_FROM_CONTENT = 0.97;

  //  왼쪽/오른쪽 완전 동일 기준(동시에 등장)
  const sharedOpacity = useTransform(scrollYProgress, [0, SHOW_START, SHOW_FULL, 1], [0, 0, 1, 1]);

  const sharedY = useTransform(scrollYProgress, [0, SHOW_START, SHOW_FULL, 1], [RISE, RISE, 0, 0]);

  const sharedScaleInd = useTransform(
    scrollYProgress,
    [0, SHOW_START, SHOW_FULL, 1],
    [SCALE_FROM_IND, SCALE_FROM_IND, 1, 1],
  );

  const sharedScaleContent = useTransform(
    scrollYProgress,
    [0, SHOW_START, SHOW_FULL, 1],
    [SCALE_FROM_CONTENT, SCALE_FROM_CONTENT, 1, 1],
  );

  const sharedBlur = useTransform(
    scrollYProgress,
    [0, SHOW_START, SHOW_FULL, 1],
    ["2px", "2px", "0px", "0px"],
  );

  //  완전 등장 이후 고정
  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => {
      if (v >= SHOW_FULL) setLocked(true);
    });
    return () => unsub();
  }, [scrollYProgress]);

  const imageIndex = Number(item.no);
  const imageSrc = `/assets/images/effect/card${imageIndex}.png`;

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.getBoundingClientRect().height;
      setContentHeight(Math.max(72, Math.ceil(h)));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={rowRef} className="relative flex items-start gap-6 md:gap-8 w-[80%] md:w-auto">
      {/*  왼쪽 인디케이터: 천천히 올라오며 등장 */}
      <motion.div
        className="relative w-[24px] shrink-0 flex flex-col items-center"
        style={{
          opacity: locked ? 1 : sharedOpacity,
          y: locked ? 0 : sharedY,
          scale: locked ? 1 : sharedScaleInd,
          filter: locked ? "blur(0px)" : (sharedBlur as any),
        }}
      >
        <div
          className="h-[28px] w-[28px] rounded-full border-[8px] border-[#fff] z-10"
          style={{ background: item.color }}
        />

        <div
          className="relative mt-[-10px] w-[9px] rounded-full bg-[#ECEBFF] overflow-hidden"
          style={{ height: contentHeight }}
        >
          <motion.div
            className="absolute left-0 bottom-0 w-full rounded-full"
            style={{
              height: fillHeight,
              background: `linear-gradient(
                0deg,
                ${item.color} 0%,
                ${item.color}80 60%,
                ${item.color}1A 100%
              )`,
            }}
          />
        </div>
      </motion.div>

      {/*  오른쪽 콘텐츠: 천천히 올라오며 등장 */}
      <motion.div
        ref={contentRef}
        className="flex flex-col md:flex-row items-start gap-8"
        style={{
          opacity: locked ? 1 : sharedOpacity,
          y: locked ? 0 : sharedY,
          scale: locked ? 1 : sharedScaleContent,
          filter: locked ? "blur(0px)" : (sharedBlur as any),
        }}
      >
        <div
          className="shadow-[5.38px_16.14px_59.77px_-7.77px_rgba(22,52,80,0.18)] flex 
            md:h-[clamp(74px,5.7vw,110px)] 
            md:w-[clamp(74px,5.7vw,110px)] 
            h-[74px] w-[74px] bg-[#FFF] items-center justify-center md:rounded-[32px] rounded-[21.4px]"
        >
          <img src={imageSrc} alt={item.title} />
        </div>

        <div className="flex flex-col gap-10">
          <div className="flex items-baseline gap-2 flex-col">
            <span className="text-[36px] md:text-display font-bold text-[#121319]">{item.no}</span>
            <span className="text-[20px] font-bold md:text-display3_1 md:font-bold text-[#121319]">
              {item.title}
            </span>
          </div>

          <ul className="space-y-2 text-sm text-[#333]">
            {item.desc.map((d) => (
              <li key={d} className="flex gap-2 items-start">
                <img src="/assets/images/icon/check.png" alt="check" width={24} height={24} />
                <span className="whitespace-pre-line font-normal text-[16px] md:text-[18px] text-[#4D4F58] break-keep leading-[1.5]">
                  {d}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col">
            {item.desc2?.map((d) => (
              <li key={d} className="flex">
                <span className="font-normal text-base text-[#4D4F58] leading-[1.5]">*</span>
                <span className="whitespace-pre-line font-normal text-[14px] md:text-base text-[#4D4F58] leading-[1.5]">
                  {d}
                </span>
              </li>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const Section2 = () => {
  const bgRef = useRef<HTMLDivElement | null>(null);
  const items: Item[] = [
    {
      no: "01",
      title: "맞춤형 서비스",
      desc: [
        `고객사의 사업 구조(B2B 플랫폼, 마켓플레이스 등)에 최적화된 결제/정산 프로세스 지원`,
        `분산 정산, 영중소 차액 정산 등 특수 조건 대응 및 비즈니스 모델 확장성 확보`,
      ],
      color: "#AEA4FF",
    },
    {
      no: "02",
      title: "통합 관리 & 자동화",
      desc: [
        `분산된 PG/VAN 결제 데이터 통합 관리 및 정산금 산정·지급 전 과정 자동화`,
        `수기 업무 제거를 통한 재무 리소스 절감 및 오기입 등 휴먼 에러 차단, 운영 안정성 확보`,
      ],
      color: "#006FFF",
    },
    {
      no: "03",
      title: "안정적 리스크 관리",
      desc: [
        `PG 수준의 고도화된 위험관리(RM) 시스템 및 실시간 이상 거래 탐지 제공`,
        `금융 사고 예방을 통한 운영 안정성 확보 및 법적/금융 규제 대응 허들 해소`,
      ],
      color: "#2600FF",
    },
    {
      no: "04",
      title: "올인원 가맹점 지원",
      desc: [
        `가맹점 전용 앱/웹을 통한 실시간 내역 확인 및 정산 서류 발급 지원`,
        `파트너 관리 시스템을 통한 통합관리 및 본사 CS 업무량 감소`,
        `SMS, QR, OCR 등 다양한 결제 방법 제공을 통한 시장 경쟁력 강화 `,
      ],
      color: "#9324BF",
    },
    {
      no: "05",
      title: "확장성 있는 영업대행 관리 지원",
      desc: [
        "총판-대리점-딜러로 이어지는 최대 5단계의 영업 구조 설정",
        "투명한 수수료 배분 시스템으로 인한 투명성 확보",
        "데이터 기반의 정산 신뢰도 확보를 통한 대규모 외부 영업대행 조직 관리 최적화",
      ],
      color: "#FF4880",
    },
    {
      no: "06",
      title: "파이낸싱 (팩토링) 지원",
      desc: [
        `실시간/선(D+1) 정산 서비스 프로그램 운영 가능`,
        `가맹점 매출 담보로 한 실시간 정산 서비스 운영 가능 `,
        `파트너사의 자금 유동성을 즉각 개선하여 가맹점 락인 효과`,
        `파트너사는 수수료 수익을 통한 추가 매출 확보 및 상호 이익 구조 형성`,
      ],
      color: "#D40004",
      desc2: [
        "선정산 : 정산 예정일 보다 먼저 정산을 받는 서비스",
        `선수금 : 보증금과 같이 서비스를 지급하기 전에  ${
          isMobile() ? "\n" : ""
        } 미리 받는 돈을 의미 `,
      ],
    },
  ];

  return (
    <section className="mx-auto w-full md:max-w-[900px] px-6 md:px-0">
      <div className="flex flex-col gap-20">
        {items.map((item, index) => (
          <Step key={item.no} item={item} bgRef={item.no === "02" ? bgRef : undefined} />
        ))}
      </div>
    </section>
  );
};
